import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import BookmarkAddRoundedIcon from '@mui/icons-material/BookmarkAddRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ElevatorRoundedIcon from '@mui/icons-material/ElevatorRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useRef, useState } from 'react';

import { useOttoOptional } from '~/components/otto/OttoProvider';
import type { KnowledgeEntryKindEnum, KnowledgeEntrySourceEnum } from '~/data/generated/model';
import { propertiesKnowledgeCreateBodyContentMax } from '~/data/generated/zod/properties/properties.zod';
import { usePropertyKnowledge, useSaveKnowledge } from '~/data/hooks/useKnowledge';

import {
  formatKnowledgeDate,
  KNOWLEDGE_KIND_LABELS,
  KNOWLEDGE_KINDS,
  KnowledgeKindChip,
  KnowledgeSourceBadge,
} from './KnowledgeBits';
import { useKnowledgeCapture } from './KnowledgeCaptureProvider';
import { useKnowledgeEnabled } from './useKnowledgeEnabled';

export interface KnowledgeChatTarget {
  propertyId: string;
  propertyName: string;
  carId?: string;
  carName?: string;
}

export interface KnowledgeDraftCardData {
  target: KnowledgeChatTarget;
  kind: KnowledgeEntryKindEnum;
  content: string;
  suggested: boolean;
}

export interface KnowledgeResultItem {
  id: string;
  kind: KnowledgeEntryKindEnum;
  content: string;
  authorName: string;
  createdAt: string;
  source: KnowledgeEntrySourceEnum;
  target: KnowledgeChatTarget;
}

export interface KnowledgeResultsCardData {
  query: string;
  target: KnowledgeChatTarget | null;
  results: KnowledgeResultItem[];
}

export interface KnowledgeContentPromptCardData {
  target: KnowledgeChatTarget | null;
  kind: KnowledgeEntryKindEnum | null;
}

export interface KnowledgeTargetOption extends KnowledgeChatTarget {
  /** Unique among the options, so the pick it sends back names one property. */
  label: string;
}

export interface KnowledgeTargetChoiceCardData {
  question: string;
  options: KnowledgeTargetOption[];
}

const knowledgeTargetText = (t: KnowledgeChatTarget) =>
  t.carName ? `${t.carName} · ${t.propertyName}` : t.propertyName;

function CardFrame({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${alpha('#000', 0.08)}`,
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ display: 'flex', color: theme.palette.primary.main }}>{icon}</Box>
        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
          {title}
        </Typography>
      </Box>
      {children}
    </Box>
  );
}

function TargetLine({ target }: { target: KnowledgeChatTarget }) {
  const Icon = target.carId ? ElevatorRoundedIcon : ApartmentRoundedIcon;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
      <Icon sx={{ fontSize: 15 }} />
      <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
        {knowledgeTargetText(target)}
      </Typography>
    </Box>
  );
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

const SAVED_DRAFTS_KEY = 'liftai.knowledge.savedDrafts:v1';

const draftKey = (sessionId: string, draftContent: string) => `${sessionId}\n${draftContent}`;

function readSavedDrafts(): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(SAVED_DRAFTS_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : [];
  } catch {
    return [];
  }
}

function markDraftSaved(key: string) {
  try {
    localStorage.setItem(SAVED_DRAFTS_KEY, JSON.stringify([...readSavedDrafts(), key].slice(-200)));
  } catch {
    // The entry is still saved; only the marker is lost.
  }
}

/** Nothing is stored until the reader clicks Save. */
export function KnowledgeDraftCard({
  data,
  sessionId,
}: {
  data: KnowledgeDraftCardData;
  sessionId?: string;
}) {
  const enabled = useKnowledgeEnabled();
  const saveKnowledge = useSaveKnowledge();
  const [kind, setKind] = useState<KnowledgeEntryKindEnum>(data.kind);
  const [content, setContent] = useState(data.content);
  const [editing, setEditing] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const savingRef = useRef(false);
  // A draft saved before, even edited or elsewhere, never offers a second save.
  const { entries } = usePropertyKnowledge(data.target.propertyId);
  const alreadySaved =
    (Boolean(sessionId) && readSavedDrafts().includes(draftKey(sessionId!, data.content))) ||
    entries.some(
      (e) =>
        e.isAuthor &&
        e.source === 'assistant' &&
        (e.carId ?? undefined) === data.target.carId &&
        e.content === data.content.trim(),
    );
  const saved = saveState === 'saved' || (alreadySaved && saveState === 'idle' && !editing);

  if (!enabled) return null;

  const onSave = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaveState('saving');
    try {
      await saveKnowledge(data.target.propertyId, {
        ...(data.target.carId ? { carId: data.target.carId } : {}),
        kind,
        content: content.trim(),
        source: 'assistant',
      });
      if (sessionId) markDraftSaved(draftKey(sessionId, data.content));
      setEditing(false);
      setSaveState('saved');
    } catch {
      setSaveState('error');
    } finally {
      savingRef.current = false;
    }
  };

  return (
    <CardFrame
      icon={<BookmarkAddRoundedIcon sx={{ fontSize: 18 }} />}
      title={data.suggested ? 'Save this as knowledge?' : 'Draft knowledge'}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <TargetLine target={data.target} />
        {editing ? null : <KnowledgeKindChip kind={kind} />}
      </Box>

      {editing ? (
        <>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={kind}
            onChange={(_, value: KnowledgeEntryKindEnum | null) => value && setKind(value)}
            aria-label="Kind"
          >
            {KNOWLEDGE_KINDS.map((k) => (
              <ToggleButton key={k} value={k} sx={{ textTransform: 'none', py: 0.25, px: 1.5 }}>
                {KNOWLEDGE_KIND_LABELS[k]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <TextField
            multiline
            minRows={2}
            size="small"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            slotProps={{
              htmlInput: {
                'aria-label': 'Knowledge text',
                maxLength: propertiesKnowledgeCreateBodyContentMax,
              },
            }}
          />
        </>
      ) : (
        <Typography variant="body2" sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
          {content}
        </Typography>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
        Source: this conversation
      </Typography>

      {saved ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
          <CheckCircleRoundedIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
            Saved to {knowledgeTargetText(data.target)}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            variant="contained"
            disabled={saveState === 'saving' || !content.trim()}
            startIcon={
              saveState === 'saving' ? <CircularProgress size={12} color="inherit" /> : null
            }
            onClick={() => void onSave()}
            sx={{ textTransform: 'none' }}
          >
            Save
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setEditing((e) => !e)}
            sx={{ textTransform: 'none' }}
          >
            {editing ? 'Done' : 'Edit'}
          </Button>
          {saveState === 'error' ? (
            <Typography variant="caption" sx={{ color: 'error.main', fontSize: '0.72rem' }}>
              Couldn&apos;t save — try again.
            </Typography>
          ) : null}
        </Box>
      )}
    </CardFrame>
  );
}

/** An empty result offers to add the missing knowledge. */
export function KnowledgeResultsCard({ data }: { data: KnowledgeResultsCardData }) {
  const enabled = useKnowledgeEnabled();
  const capture = useKnowledgeCapture();
  const { target } = data;
  if (!enabled) return null;

  if (data.results.length === 0) {
    return (
      <CardFrame icon={<MenuBookRoundedIcon sx={{ fontSize: 18 }} />} title="Recorded knowledge">
        {target ? <TargetLine target={target} /> : null}
        <Typography variant="body2" color="text.secondary">
          No entries match.
        </Typography>
        {capture ? (
          <Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddRoundedIcon />}
              sx={{ textTransform: 'none' }}
              onClick={() =>
                capture.openAddKnowledge(
                  target
                    ? { target: { propertyId: target.propertyId, carId: target.carId } }
                    : undefined,
                )
              }
            >
              Add knowledge
            </Button>
          </Box>
        ) : null}
      </CardFrame>
    );
  }

  return (
    <CardFrame icon={<MenuBookRoundedIcon sx={{ fontSize: 18 }} />} title="Recorded knowledge">
      {data.results.map((r) => (
        <Box
          key={r.id}
          data-testid="knowledge-result"
          sx={{
            pt: 1,
            borderTop: `1px solid ${alpha('#000', 0.06)}`,
            '&:first-of-type': { pt: 0, borderTop: 'none' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
            <KnowledgeKindChip kind={r.kind} />
            <KnowledgeSourceBadge source={r.source} />
          </Box>
          <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
            {r.content}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
            {r.target.carId ? (
              <ElevatorRoundedIcon sx={{ fontSize: 14 }} />
            ) : (
              <ApartmentRoundedIcon sx={{ fontSize: 14 }} />
            )}
            <Typography variant="caption">
              {knowledgeTargetText(r.target)} · {r.authorName} · {formatKnowledgeDate(r.createdAt)}
            </Typography>
          </Box>
        </Box>
      ))}
    </CardFrame>
  );
}

export function KnowledgeTargetChoiceCard({
  data,
  sessionId,
}: {
  data: KnowledgeTargetChoiceCardData;
  sessionId?: string;
}) {
  const enabled = useKnowledgeEnabled();
  const otto = useOttoOptional();
  const [picked, setPicked] = useState<string | null>(null);
  if (!enabled) return null;

  return (
    <CardFrame icon={<HelpOutlineRoundedIcon sx={{ fontSize: 18 }} />} title={data.question}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {data.options.map((option) => {
          const key = option.carId ?? option.propertyId;
          return (
            <Button
              key={key}
              size="small"
              variant={picked === key ? 'contained' : 'outlined'}
              disabled={(picked !== null && picked !== key) || !otto || !sessionId}
              startIcon={<ApartmentRoundedIcon />}
              sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
              onClick={() => {
                if (!otto || !sessionId || picked !== null) return;
                // Lock only once sent; while Otto is still writing the send is refused.
                if (otto.sendMessage(sessionId, option.label)) setPicked(key);
              }}
            >
              {option.label}
            </Button>
          );
        })}
      </Box>
    </CardFrame>
  );
}

export function KnowledgeContentPromptCard({ data }: { data: KnowledgeContentPromptCardData }) {
  const enabled = useKnowledgeEnabled();
  const capture = useKnowledgeCapture();
  const { target } = data;
  if (!enabled) return null;

  return (
    <CardFrame
      icon={<BookmarkAddRoundedIcon sx={{ fontSize: 18 }} />}
      title="What should I record?"
    >
      {target ? <TargetLine target={target} /> : null}
      <Typography variant="body2" color="text.secondary">
        Reply with the details, or fill in the form.
      </Typography>
      {capture ? (
        <Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddRoundedIcon />}
            sx={{ textTransform: 'none' }}
            onClick={() =>
              capture.openAddKnowledge({
                ...(target
                  ? { target: { propertyId: target.propertyId, carId: target.carId } }
                  : {}),
                ...(data.kind ? { kind: data.kind } : {}),
              })
            }
          >
            Add knowledge
          </Button>
        </Box>
      ) : null}
    </CardFrame>
  );
}
